import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

/** Mirrors the backend's AtmActionType enum (prisma schema). */
export type AtmActionType = 'cash_in' | 'cash_out' | 'registration';

export const ATM_ACTION_TYPES: AtmActionType[] = [
  'cash_in',
  'cash_out',
  'registration',
];

export interface AtmActionHistoryUserRef {
  id: number;
  username: string;
}

export interface AtmActionHistoryProviderRef {
  id: number;
  name: string;
}

export interface AtmActionHistoryEntry {
  id: number;
  createdAt: string;
  action: AtmActionType;
  amount: number | null;
  atmId: number;
  atm: AtmActionHistoryUserRef;
  atmProviderId: number;
  atmProvider: AtmActionHistoryProviderRef;
  /** Set for internal providers, where the player is a platform user. */
  userId: number | null;
  user: AtmActionHistoryUserRef | null;
  /** Set for terminal providers, where the player exists only provider-side. */
  atmExternalUserId: number | null;
  atmExternalUser: AtmActionHistoryUserRef | null;
}

export interface AtmActionHistoryPageInfo {
  offset: number;
  limit: number;
  nextOffset: number | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
}

export interface AtmActionHistorySummary {
  totalCashIn: number;
  totalCashOut: number;
  /** Cash in minus cash out. Negative in a heavy payout window. */
  profit: number;
  totalRegistrations: number;
  /** Effective range, after the 7-day default is applied. ISO-8601. */
  from: string;
  to: string;
}

export interface PaginatedAtmActionHistory {
  items: AtmActionHistoryEntry[];
  pageInfo: AtmActionHistoryPageInfo;
  summary: AtmActionHistorySummary;
}

export interface AtmActionHistoryFilters {
  atmId?: number;
  userId?: number;
  atmExternalUserId?: number;
  providerId?: number;
  action?: AtmActionType;
  /** Matches either a platform username or a provider-side account name. */
  username?: string;
  /** ISO-8601; the backend field is a DateTime. */
  from?: string;
  to?: string;
  /** Backend caps this at 200. */
  limit?: number;
  offset?: number;
}

// Every field the list and the detail panel show. Kept in one fragment so the
// two queries can't drift apart.
const ATM_ACTION_HISTORY_FIELDS = gql`
  fragment AtmActionHistoryFields on AtmActionHistoryEntity {
    id
    createdAt
    action
    amount
    atmId
    atm {
      id
      username
    }
    atmProviderId
    atmProvider {
      id
      name
    }
    userId
    user {
      id
      username
    }
    atmExternalUserId
    atmExternalUser {
      id
      username
    }
  }
`;

export const ATM_ACTION_HISTORY = gql`
  query AtmActionHistory($filters: AtmActionHistoryFiltersInput) {
    atmActionHistory(filters: $filters) {
      items {
        ...AtmActionHistoryFields
      }
      pageInfo {
        offset
        limit
        nextOffset
        hasNextPage
        hasPreviousPage
        total
      }
      summary {
        totalCashIn
        totalCashOut
        profit
        totalRegistrations
        from
        to
      }
    }
  }
  ${ATM_ACTION_HISTORY_FIELDS}
`;

export const ATM_ACTION_HISTORY_ENTRY = gql`
  query AtmActionHistoryEntry($id: Int!) {
    atmActionHistoryEntry(id: $id) {
      ...AtmActionHistoryFields
    }
  }
  ${ATM_ACTION_HISTORY_FIELDS}
`;

class AtmActionHistoryService {
  private static instance: AtmActionHistoryService;

  static getInstance(): AtmActionHistoryService {
    if (!AtmActionHistoryService.instance) {
      AtmActionHistoryService.instance = new AtmActionHistoryService();
    }
    return AtmActionHistoryService.instance;
  }

  async getHistory(
    filters: AtmActionHistoryFilters = {},
  ): Promise<PaginatedAtmActionHistory> {
    const { data } = await apolloClient.query<{
      atmActionHistory: PaginatedAtmActionHistory;
    }>({
      query: ATM_ACTION_HISTORY,
      variables: { filters },
      // A log that is appended to constantly: a cached page is a stale page.
      fetchPolicy: 'network-only',
    });
    return data.atmActionHistory;
  }

  async getEntry(id: number): Promise<AtmActionHistoryEntry | null> {
    const { data } = await apolloClient.query<{
      atmActionHistoryEntry: AtmActionHistoryEntry | null;
    }>({
      query: ATM_ACTION_HISTORY_ENTRY,
      variables: { id },
      fetchPolicy: 'network-only',
    });
    return data.atmActionHistoryEntry;
  }
}

export const atmActionHistoryService = AtmActionHistoryService.getInstance();
